import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { sitePath } from "../../lib/siteRouting";
import publicApi from "../../api/public";
import { ArrowRight, Building2, Home, ShoppingBag, MapPin } from "lucide-react";
import { SmartImage } from "./SmartImage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsMegaMenu({ open, onClose }) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (open && projects.length === 0) {
      publicApi.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
    }
  }, [open, projects.length]);

  if (!open) return null;

  return (
    <div
      className="absolute top-full left-0 right-0 bg-white border-b border-[#E2E5F1] shadow-2xl z-30 animate-in fade-in slide-in-from-top-2 duration-300"
      onMouseLeave={onClose}
      data-testid="megamenu"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-10">
        {/* Catégories à gauche */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-3">// Explorer</div>
          <Link to={sitePath("/projets")} onClick={onClose} className="group flex items-start gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition" data-testid="megamenu-tous">
            <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-bold text-[#0F1A3D] tracking-tight flex items-center gap-1 group-hover:gap-2 transition-all" style={{ fontFamily: "'Playfair Display', serif" }}>
                Tous les projets <ArrowRight className="w-3.5 h-3.5 text-[#1E3A8A]" />
              </div>
              <div className="text-xs text-[#4B5775] mt-0.5">Vue d'ensemble des résidences et programmes neufs.</div>
            </div>
          </Link>
          <a href="https://vente.tpco.sn" className="group flex items-start gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition" data-testid="megamenu-catalogue">
            <div className="w-10 h-10 rounded-lg bg-[#E91E63] text-white flex items-center justify-center shrink-0">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-bold text-[#0F1A3D] tracking-tight flex items-center gap-1 group-hover:gap-2 transition-all" style={{ fontFamily: "'Playfair Display', serif" }}>
                Catalogue des biens <ArrowRight className="w-3.5 h-3.5 text-[#E91E63]" />
              </div>
              <div className="text-xs text-[#4B5775] mt-0.5">Appartements, maisons, studios à vendre ou louer.</div>
            </div>
          </a>
          <Link to={sitePath("/contact")} onClick={onClose} className="group flex items-start gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition" data-testid="megamenu-surmesure">
            <div className="w-10 h-10 rounded-lg bg-[#2D7A3F] text-white flex items-center justify-center shrink-0">
              <Home className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-bold text-[#0F1A3D] tracking-tight flex items-center gap-1 group-hover:gap-2 transition-all" style={{ fontFamily: "'Playfair Display', serif" }}>
                Construction sur-mesure <ArrowRight className="w-3.5 h-3.5 text-[#2D7A3F]" />
              </div>
              <div className="text-xs text-[#4B5775] mt-0.5">Votre maison personnalisée, du terrain à la livraison.</div>
            </div>
          </Link>
        </div>

        {/* 3 cartes de projets phares */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-3">// Derniers projets</div>
          <div className="grid grid-cols-3 gap-4">
            {projects.slice(0, 3).map((p) => (
              <Link
                key={p.id}
                to={sitePath(`/projet/${p.slug || p.id}`)}
                onClick={onClose}
                className="group block rounded-lg border border-[#E2E5F1] overflow-hidden hover:border-[#1E3A8A] transition"
                data-testid={`megamenu-project-${p.id}`}
              >
                <div className="overflow-hidden">
                  <div className="img-zoom-hover">
                    <SmartImage
                      src={p.cover_url ? `${BACKEND}${p.cover_url}` : null}
                      alt={p.nom}
                      aspectRatio="aspect-[4/3]"
                      fallbackLabel={p.type === "maison" ? "Lot" : "Résidence"}
                    />
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-[9px] uppercase tracking-wider text-[#E91E63] font-bold">
                    {p.type === "maison" ? "Lot maisons" : "Résidence"}
                  </div>
                  <div className="font-display text-sm font-bold text-[#0F1A3D] line-clamp-1 mt-0.5 group-hover:text-[#1E3A8A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {p.nom}
                  </div>
                  <div className="text-[10px] text-[#4B5775] mt-1 flex items-center gap-1 line-clamp-1">
                    <MapPin className="w-2.5 h-2.5 text-[#1E3A8A]" /> {p.quartier ? `${p.quartier}, ${p.ville}` : p.ville}
                  </div>
                </div>
              </Link>
            ))}
            {projects.length === 0 && (
              <div className="col-span-3 text-center py-8 text-xs text-[#4B5775]">Chargement…</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
